import { ACTIVITY_EFFECTIVENESS_SEGMENTS as EFFECTIVENESS_SEGMENTS } from '../helpers/activityEffectivenessSegments'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import Tooltip from '../components/ReportTooltip'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { Storefront, Tag } from '@phosphor-icons/react'
import dayjs from '../../../utils/dayjsConfig'
import FilterUbiquaMui from '../../../components/Filters/FilterUbiquaMui/FilterUbiquaMui'
import { MapModalProvider } from '../../hook/MapModalProvider'
import GeoCardShowMap from '../../components/GeoCardShowMap'
import { formatMoney, getLocale } from '../../../helpers/utils'
import { MapPin, ArrowUpRight, ArrowUUpLeft } from '@phosphor-icons/react'
import { RouteVisitEffectivenessCard } from '../components/RouteExecutionKpis'
import { buildActivityEffectivenessValues } from '../helpers/routeKpiValues'
import { ACTIVITY_FILTER_KEYS, buildActivityTableRows } from '../helpers/activityBreakdownFilters'
import {
  buildAppliedDateRange,
  buildSelectValue,
  createEmptyDateRange,
  dateRangeToPickerValue,
  dateRangeToQuery,
  formatDateRangeSummary,
  getSelectValues,
  normalizeDateRange,
  pickerValueToDateRange
} from '../helpers/routeExecutionFilters'
import { exportActivityBreakdownTableCsv } from '../helpers/exportActivityBreakdownTableCsv'
import { ACTIVITIES_OPTIONS, BOOLEAN_OPTIONS } from '../helpers/userReportTabs'
import RouteExecutionDateRangeCard from '../components/RouteExecutionDateRangeCard'
import ActivityBreakdownTable from '../components/ActivityBreakdownTable'
import ActivityReportMapModal from '../components/maps/ActivityReportMapModal'
import { buildActivityDetailUrl } from '../helpers/buildUnifiedVisitDetails'

const haveSameValues = (current = [], next = []) =>
  current.length === next.length && current.every((value) => next.includes(value))

const KPI_ACTIVITY_BY_SEGMENT = {
  order: ['PEDIDO', 'order'],
  charge: ['COBRO', 'charge'],
  delivery: ['ENTREGA', 'delivery']
}

const ActivityBreakdownTab = ({
  data = [],
  perPage = 25,
  currentPage = 1,
  setCurrentPage,
  totalPages = 0,
  totalCount = 0,
  handlePaginationClick,
  salesmanName,
  startDate,
  endDate,
  overview,
  effectivenessSegment: controlledEffectivenessSegment,
  onEffectivenessSegmentChange,
  activityMapData = [],
  onLoadActivityMapData,
  formatActivityName,
  isMobileLayout = false,
  initialExpandedActivityKey = null,
  searchQuery = '',
  onSearchQuery,
  routeFilterData,
  routeFilterLoading = false,
  routeFilterHasMore = false,
  routeFilterSearchQuery = '',
  onRouteFilterSearch,
  onRouteFilterLoadMore,
  isActivityInfoFetching = false,
  isKpiUpdating = false,
  filterSelections,
  onFilterSelectionsChange,
  activityDateQuery,
  setActivityDateQuery,
  selectedActivityDates: persistedActivityDates,
  onSelectedActivityDatesChange,
  selectedVisitKpi: persistedSelectedVisitKpi,
  onSelectedVisitKpiChange,
  onActivityKpiClientQueryChange,
  onRowsPerPageChange
}) => {
  const navigate = useNavigate()
  const [show, setShow] = useState(false)
  const [dataSelected, setDataSelected] = useState(false)
  const [expandedActivityKey, setExpandedActivityKey] = useState(initialExpandedActivityKey)
  const [shouldRenderMobileMap, setShouldRenderMobileMap] = useState(false)
  const searchInput = useRef(null)
  const [query, setQuery] = useState(searchQuery || '')
  const [localFilterSelections, setLocalFilterSelections] = useState(() => new Map())
  const [filterVersion, setFilterVersion] = useState(0)
  const allSelectedFilterOptions = filterSelections ?? localFilterSelections
  const setAllSelectedFilterOptions = onFilterSelectionsChange ?? setLocalFilterSelections
  const [localActivityDates, setLocalActivityDates] = useState(null)
  const selectedActivityDates =
    persistedActivityDates === undefined ? localActivityDates : persistedActivityDates
  const setSelectedActivityDates = onSelectedActivityDatesChange ?? setLocalActivityDates
  const [draftActivityDateRange, setDraftActivityDateRange] = useState(() => createEmptyDateRange())
  const [localSelectedVisitKpi, setLocalSelectedVisitKpi] = useState(null)
  const selectedVisitKpi =
    persistedSelectedVisitKpi === undefined ? localSelectedVisitKpi : persistedSelectedVisitKpi
  const setSelectedVisitKpi = onSelectedVisitKpiChange ?? setLocalSelectedVisitKpi
  const hasAppliedKpiClientFilter = useRef(false)
  const selectedKpiPeriod = useRef({ startDate, endDate })
  const [localEffectivenessSegment, setLocalEffectivenessSegment] = useState('general')
  const effectivenessSegment = controlledEffectivenessSegment ?? localEffectivenessSegment
  const setEffectivenessSegment = onEffectivenessSegmentChange ?? setLocalEffectivenessSegment
  const selectedActivityFilter = allSelectedFilterOptions.get(ACTIVITY_FILTER_KEYS.activity)
  const selectedActivityValues = Array.isArray(selectedActivityFilter)
    ? selectedActivityFilter
    : getSelectValues(selectedActivityFilter)
  const selectedClientFilter = allSelectedFilterOptions.get(ACTIVITY_FILTER_KEYS.client)
  const selectedClientValues = Array.isArray(selectedClientFilter)
    ? selectedClientFilter
    : getSelectValues(selectedClientFilter)
  const effectivenessSegments = useMemo(() => {
    const serverSegments = Object.keys(overview?.activity_effectiveness || {})
    const available = serverSegments.length
      ? serverSegments
      : ['general', ...(routeFilterData?.activities || [])]
    return [...new Set(available)].filter((key) => EFFECTIVENESS_SEGMENTS[key])
  }, [overview?.activity_effectiveness, routeFilterData?.activities])
  const activeEffectivenessSegment = effectivenessSegments.includes(effectivenessSegment)
    ? effectivenessSegment
    : 'general'

  useEffect(() => {
    if (effectivenessSegment !== activeEffectivenessSegment)
      setEffectivenessSegment(activeEffectivenessSegment)
  }, [effectivenessSegment, activeEffectivenessSegment, setEffectivenessSegment])

  const periodStart = useMemo(
    () => (startDate ? dayjs(startDate).startOf('day') : null),
    [startDate]
  )
  const periodEnd = useMemo(
    () => (endDate ? dayjs(endDate).startOf('day') : dayjs().startOf('day')),
    [endDate]
  )

  const constrainDateRange = useCallback(
    (range) => {
      const normalized = normalizeDateRange(range)
      if (
        (normalized.end && periodStart && normalized.end.isBefore(periodStart, 'day')) ||
        (normalized.start && normalized.start.isAfter(periodEnd, 'day'))
      ) {
        return createEmptyDateRange()
      }
      return normalizeDateRange({
        start:
          normalized.start && periodStart && normalized.start.isBefore(periodStart, 'day')
            ? periodStart
            : normalized.start,
        end: normalized.end && normalized.end.isAfter(periodEnd, 'day') ? periodEnd : normalized.end
      })
    },
    [periodStart, periodEnd]
  )

  useEffect(() => {
    const previous = pickerValueToDateRange(selectedActivityDates)
    const bounded = constrainDateRange(previous)
    const nextQuery = dateRangeToQuery(bounded, 'activity_start', 'activity_end')
    if (nextQuery !== dateRangeToQuery(previous, 'activity_start', 'activity_end')) {
      setSelectedActivityDates(dateRangeToPickerValue(bounded))
      setActivityDateQuery?.(nextQuery)
      setCurrentPage?.(1)
    }
    setDraftActivityDateRange((range) => constrainDateRange(range))
  }, [
    constrainDateRange,
    selectedActivityDates,
    setActivityDateQuery,
    setCurrentPage,
    setSelectedActivityDates
  ])

  useEffect(() => {
    if (searchInput.current) {
      searchInput.current.value = searchQuery || ''
    }
    setQuery(searchQuery || '')
  }, [searchQuery])

  const deactivateVisitKpi = useCallback(() => {
    setSelectedVisitKpi(null)
    onActivityKpiClientQueryChange?.('')
    setCurrentPage?.(1)
    hasAppliedKpiClientFilter.current = false
  }, [onActivityKpiClientQueryChange, setCurrentPage, setSelectedVisitKpi])

  const clearVisitKpi = useCallback(() => {
    const shouldClearKpiClients = hasAppliedKpiClientFilter.current
    deactivateVisitKpi()
    if (shouldClearKpiClients) {
      setAllSelectedFilterOptions((current) => {
        const next = new Map(current)
        next.set(ACTIVITY_FILTER_KEYS.client, [])
        next.set(ACTIVITY_FILTER_KEYS.activity, [])
        return next
      })
    }
  }, [deactivateVisitKpi, setAllSelectedFilterOptions])

  const resetFilterContext = () => {
    setAllSelectedFilterOptions(new Map())
    setActivityDateQuery?.(null)
    setSelectedActivityDates(null)
    setDraftActivityDateRange(createEmptyDateRange())
    setQuery('')
    if (searchInput.current) searchInput.current.value = ''
    onSearchQuery?.('')
    setCurrentPage?.(1)
    setFilterVersion((version) => version + 1)
  }

  const selectVisitKpi = (key) => {
    resetFilterContext()
    if (selectedVisitKpi === key) {
      clearVisitKpi()
      return
    }
    setSelectedVisitKpi(key)
    setCurrentPage?.(1)
  }

  useEffect(() => {
    if (!selectedVisitKpi) {
      onActivityKpiClientQueryChange?.('')
      if (hasAppliedKpiClientFilter.current) {
        setAllSelectedFilterOptions((current) => {
          const next = new Map(current)
          next.set(ACTIVITY_FILTER_KEYS.client, [])
          return next
        })
        hasAppliedKpiClientFilter.current = false
      }
      return
    }
    const segment = overview?.activity_effectiveness?.[activeEffectivenessSegment] || {}
    const clientKeys = {
      planned: 'planned_client_uids',
      attendedInRoute: 'attended_in_route_client_uids',
      unattended: 'unattended_in_route_client_uids',
      outside: 'attended_out_of_route_client_uids'
    }
    const clientIds = segment[clientKeys[selectedVisitKpi]] || []
    const segmentActivityCandidates = KPI_ACTIVITY_BY_SEGMENT[activeEffectivenessSegment] || []
    const availableActivities = routeFilterData?.activities || []
    const segmentActivity =
      segmentActivityCandidates.find((activity) => availableActivities.includes(activity)) ||
      segmentActivityCandidates[0]
    // El selector Cliente es la fuente visible del filtro aplicado. De esta forma
    // se pueden verificar los UID que el KPI recibió en el resumen. General no
    // restringe actividades; cada segmento específico sí valida su actividad.
    setAllSelectedFilterOptions(() => {
      const next = new Map()
      // El estado compartido de filtros guarda arreglos; el selector construye
      // su SelectValue a partir de ellos al renderizarse.
      next.set(ACTIVITY_FILTER_KEYS.client, [...clientIds])
      next.set(ACTIVITY_FILTER_KEYS.activity, segmentActivity ? [segmentActivity] : [])
      return next
    })
    hasAppliedKpiClientFilter.current = true
    // El filtro de Cliente construye el parámetro client_uid; evitamos duplicarlo.
    onActivityKpiClientQueryChange?.('')
  }, [
    selectedVisitKpi,
    activeEffectivenessSegment,
    overview,
    routeFilterData?.activities,
    onActivityKpiClientQueryChange,
    setAllSelectedFilterOptions
  ])

  useEffect(() => {
    if (
      selectedKpiPeriod.current.startDate !== startDate ||
      selectedKpiPeriod.current.endDate !== endDate
    ) {
      selectedKpiPeriod.current = { startDate, endDate }
      clearVisitKpi()
    }
  }, [startDate, endDate, clearVisitKpi])

  const activityOptions = useMemo(() => {
    const serverActivities = routeFilterData?.activities || []
    return serverActivities.map((activity) => {
      const legacyOption = ACTIVITIES_OPTIONS.find((option) => option.value === activity)
      return legacyOption || { name: formatActivityName(activity), value: activity }
    })
  }, [formatActivityName, routeFilterData?.activities])

  const tableRows = useMemo(
    () => buildActivityTableRows(data, formatActivityName),
    [data, formatActivityName]
  )

  const appliedFilterSelections = useMemo(
    () => ({
      [ACTIVITY_FILTER_KEYS.client]: buildSelectValue(
        allSelectedFilterOptions.get(ACTIVITY_FILTER_KEYS.client)
      ),
      [ACTIVITY_FILTER_KEYS.activity]: buildSelectValue(
        allSelectedFilterOptions.get(ACTIVITY_FILTER_KEYS.activity)
      ),
      [ACTIVITY_FILTER_KEYS.geofence]: buildSelectValue(
        allSelectedFilterOptions.get(ACTIVITY_FILTER_KEYS.geofence)
      )
    }),
    [allSelectedFilterOptions]
  )

  const appliedActivityDateRange = useMemo(
    () => buildAppliedDateRange(pickerValueToDateRange(selectedActivityDates)),
    [selectedActivityDates]
  )

  const supplementaryAppliedGroups = useMemo(() => {
    const groups = []
    if (appliedActivityDateRange.start) {
      groups.push({
        key: 'activity-range',
        label: 'Fecha de actividad',
        values: [
          {
            value: 'activity-range',
            label: formatDateRangeSummary(appliedActivityDateRange, '')
          }
        ],
        onRemove: () => {
          if (selectedVisitKpi) deactivateVisitKpi()
          setActivityDateQuery?.(null)
          setSelectedActivityDates(null)
          setCurrentPage?.(1)
        }
      })
    }
    return groups
  }, [
    appliedActivityDateRange,
    deactivateVisitKpi,
    selectedVisitKpi,
    setActivityDateQuery,
    setCurrentPage,
    setSelectedActivityDates
  ])

  const supplementaryActiveCount = supplementaryAppliedGroups.length
  const supplementaryDraftCount = buildAppliedDateRange(constrainDateRange(draftActivityDateRange))
    .start
    ? 1
    : 0

  const ubiquaFilters = useMemo(
    () => [
      {
        key: ACTIVITY_FILTER_KEYS.client,
        label: 'Cliente',
        icon: Storefront,
        inline: true,
        searchPlaceholder: 'Buscar ...',
        options: [
          ...(routeFilterData?.clients || [])
            .map((client) => {
              const label = `${client.uid} - ${client.name}`
              return { label, value: client.uid }
            })
            .filter((option) => option.value),
          // La lista de opciones llega paginada. Se añaden los UID del KPI que
          // aún no estén cargados para que se vean marcados al abrir Cliente.
          ...selectedClientValues
            .filter((uid) => !(routeFilterData?.clients || []).some((client) => client.uid === uid))
            .map((uid) => ({ label: uid, value: uid }))
        ],
        loading: routeFilterLoading,
        hasMore: routeFilterHasMore,
        remoteSearchQuery: routeFilterSearchQuery,
        onSearchChange: onRouteFilterSearch,
        onLoadMore: onRouteFilterLoadMore
      },
      {
        key: ACTIVITY_FILTER_KEYS.activity,
        label: 'Actividad',
        icon: Tag,
        inline: true,
        options: activityOptions.map((option) => ({
          label: option.name,
          value: option.value
        }))
      },
      {
        key: ACTIVITY_FILTER_KEYS.geofence,
        label: 'Dentro de geocerca',
        inline: true,
        options: BOOLEAN_OPTIONS.map((option) => ({ label: option.name, value: option.value }))
      }
    ],
    [
      activityOptions,
      onRouteFilterLoadMore,
      onRouteFilterSearch,
      routeFilterData?.clients,
      routeFilterHasMore,
      routeFilterLoading,
      routeFilterSearchQuery,
      selectedClientValues
    ]
  )

  const handleUbiquaFilterChange = (filterKey, nextValue) => {
    const nextValues = getSelectValues(nextValue)
    const currentValues = getSelectValues(allSelectedFilterOptions.get(filterKey))
    if (selectedVisitKpi && !haveSameValues(currentValues, nextValues)) {
      // El usuario cambió el contexto del KPI. Conservamos su selección manual,
      // pero quitamos el estado activo que representa el conjunto anterior.
      deactivateVisitKpi()
    }
    setAllSelectedFilterOptions((prevOptions) => {
      const updatedOptions = new Map(prevOptions)
      updatedOptions.set(filterKey, nextValues)
      return updatedOptions
    })
  }

  const applyDesktopFilters = (selections) => {
    const nextActivityDateRange = buildAppliedDateRange(constrainDateRange(draftActivityDateRange))
    const nextActivityDateQuery = dateRangeToQuery(
      nextActivityDateRange,
      'activity_start',
      'activity_end'
    )
    const filtersChanged = Object.values(ACTIVITY_FILTER_KEYS).some(
      (filterKey) =>
        !haveSameValues(
          getSelectValues(allSelectedFilterOptions.get(filterKey)),
          getSelectValues(selections[filterKey])
        )
    )
    const dateChanged =
      nextActivityDateQuery !==
      dateRangeToQuery(
        pickerValueToDateRange(selectedActivityDates),
        'activity_start',
        'activity_end'
      )
    if (selectedVisitKpi && (filtersChanged || dateChanged)) deactivateVisitKpi()

    const nextSelectedFilterOptions = new Map()
    Object.values(ACTIVITY_FILTER_KEYS).forEach((filterKey) => {
      nextSelectedFilterOptions.set(filterKey, [...getSelectValues(selections[filterKey])])
    })
    setAllSelectedFilterOptions(nextSelectedFilterOptions)
    setSelectedActivityDates(dateRangeToPickerValue(nextActivityDateRange))
    setActivityDateQuery?.(nextActivityDateQuery)
    setCurrentPage?.(1)
  }

  const resetReactFilters = () => {
    clearVisitKpi()
    resetFilterContext()
  }

  const isAnyFilterSelected = () => {
    const filtersSelected = Array.from(allSelectedFilterOptions.entries()).some(
      ([, value]) => value.length > 0
    )
    const activityDateSelected = Boolean(activityDateQuery?.trim())
    return filtersSelected || activityDateSelected || Boolean(searchQuery)
  }

  const submitSearch = (value) => {
    const normalizedValue = value.toLowerCase()
    if (selectedVisitKpi && normalizedValue !== (searchQuery || '')) deactivateVisitKpi()
    setQuery(normalizedValue)
    setCurrentPage?.(1)
    onSearchQuery?.(normalizedValue)
  }

  const handleUbiquaSearchChange = (event) => {
    const value = event.target.value
    setQuery(value)
    if (searchInput.current) searchInput.current.value = value

    // Al borrar el texto manualmente (incluido Ctrl/Cmd+A + Supr), no hay Enter
    // ni clic en la X. Sólo restablecemos si había una búsqueda aplicada para
    // evitar volver a consultar mientras el usuario simplemente está editando.
    if (!value && searchQuery) {
      submitSearch('')
    }
  }

  const handleUbiquaSearchEnter = (event) => {
    if (event.key === 'Enter') submitSearch(query)
  }

  const deleteSearch = () => {
    if (selectedVisitKpi && searchQuery) deactivateVisitKpi()
    setQuery('')
    setCurrentPage?.(1)
    if (searchInput.current) searchInput.current.value = ''
    onSearchQuery?.('')
  }

  const updateActivityFilter = (activityValue) => {
    deactivateVisitKpi()
    resetFilterContext()
    setAllSelectedFilterOptions(
      activityValue ? new Map([[ACTIVITY_FILTER_KEYS.activity, [activityValue]]]) : new Map()
    )
  }

  const handleKpiSelect = (key) => {
    selectVisitKpi(key)
  }

  const handleClose = () => setShow(false)

  const handleShow = (model, event) => {
    event.preventDefault()
    setDataSelected(model)
    setShow(true)
  }

  const handleTransactionClick = (event, model) => {
    event.preventDefault()
    const url = buildActivityDetailUrl(model.activity_name, model.activity_uid)
    if (url) navigate(url)
  }

  const toggleExpandedActivity = (activityUid) => {
    setExpandedActivityKey((prev) => (prev === activityUid ? null : activityUid))
  }

  useEffect(() => {
    if (!show || !dataSelected) {
      setShouldRenderMobileMap(false)
      return
    }
    if (!isMobileLayout) {
      setShouldRenderMobileMap(true)
      return
    }
    setShouldRenderMobileMap(false)
    const timeoutId = window.setTimeout(() => setShouldRenderMobileMap(true), 90)
    return () => window.clearTimeout(timeoutId)
  }, [show, dataSelected, isMobileLayout])

  const formatCountLabel = (value, label) =>
    `${new Intl.NumberFormat(getLocale()).format(Number(value) || 0)} ${label}`

  const buildKpiData = () => {
    if (!overview) return false
    return {
      geodata: `${overview?.geofence_effective_clients_count}/${overview?.geofence_location_available_count}`,
      totalOrder: formatMoney(Number(overview?.total_order)),
      totalOrderCount: Number(overview?.total_order_count ?? 0),
      totalRefundCount: Number(overview?.total_refund_count ?? 0),
      totalChargeCount: Number(overview?.total_charge_count ?? 0),
      totalDeliveryCount: Number(overview?.total_delivery_count ?? 0),
      totalRefund: formatMoney(Number(overview?.total_refund)),
      totalCharge: formatMoney(Number(overview?.total_charge)),
      totalDelivery: formatMoney(Number(overview?.total_delivery))
    }
  }

  const cardsSection = (kpiData) => {
    const hasKpiData = Boolean(kpiData)
    const isEmpty = !hasKpiData
    const geofenceValue = isEmpty || !hasKpiData ? '0/0' : kpiData.geodata
    const traceabilityCards = [
      {
        key: 'orders',
        title: 'Total Pedidos',
        value: isEmpty ? formatMoney(0) : kpiData.totalOrder,
        helperText: formatCountLabel(isEmpty ? 0 : kpiData.totalOrderCount, 'pedidos'),
        count: isEmpty ? 0 : kpiData.totalOrderCount,
        icon: <ShoppingCartOutlinedIcon fontSize="small" />
      },
      {
        key: 'refunds',
        title: 'Total Devoluciones',
        value: isEmpty ? formatMoney(0) : kpiData.totalRefund,
        helperText: formatCountLabel(isEmpty ? 0 : kpiData.totalRefundCount, 'devoluciones'),
        count: isEmpty ? 0 : kpiData.totalRefundCount,
        icon: <ArrowUUpLeft size={20} />
      },
      {
        key: 'charges',
        title: 'Total Cobros',
        value: isEmpty ? formatMoney(0) : kpiData.totalCharge,
        helperText: formatCountLabel(isEmpty ? 0 : kpiData.totalChargeCount, 'cobros'),
        count: isEmpty ? 0 : kpiData.totalChargeCount,
        icon: <PaymentsOutlinedIcon fontSize="small" />
      },
      {
        key: 'deliveries',
        title: 'Total Entregas',
        value: isEmpty ? formatMoney(0) : kpiData.totalDelivery,
        helperText: formatCountLabel(isEmpty ? 0 : kpiData.totalDeliveryCount, 'entregas'),
        count: isEmpty ? 0 : kpiData.totalDeliveryCount,
        icon: <LocalShippingOutlinedIcon fontSize="small" />
      }
    ].filter((card) => card.count > 0)

    const activityByCard = {
      orders: ['PEDIDO', 'order'],
      refunds: ['DEVOLUCIÓN', 'refund'],
      charges: ['COBRO', 'charge'],
      deliveries: ['ENTREGA', 'delivery']
    }
    const hasOtherFilters =
      Array.from(allSelectedFilterOptions.entries()).some(
        ([key, value]) =>
          key !== ACTIVITY_FILTER_KEYS.activity &&
          (Array.isArray(value) ? value : getSelectValues(value)).length > 0
      ) ||
      Boolean(activityDateQuery?.trim()) ||
      Boolean(selectedActivityDates?.length) ||
      Boolean(searchQuery?.trim())

    return (
      <>
        <div
          className={`rp-kpis activity-summary${isKpiUpdating ? ' rp-kpis-updating' : ''}`}
          aria-label="Indicadores de actividades"
          aria-busy={isKpiUpdating}
        >
          {isKpiUpdating ? (
            <span className="visually-hidden" role="status">
              Actualizando indicadores
            </span>
          ) : null}
          <RouteVisitEffectivenessCard
            values={{
              ...buildActivityEffectivenessValues(overview, activeEffectivenessSegment),
              ...EFFECTIVENESS_SEGMENTS[activeEffectivenessSegment],
              info: EFFECTIVENESS_SEGMENTS[activeEffectivenessSegment].formula,
              variant: 'attendance'
            }}
            selected={selectedVisitKpi}
            onSelect={handleKpiSelect}
            selectableMetricIds={['attendedInRoute', 'outside']}
            headerAction={
              <ToggleButtonGroup
                exclusive
                size="small"
                className="rp-segment"
                aria-label="Actividad de efectividad"
                value={activeEffectivenessSegment}
                onChange={(_, value) => value && setEffectivenessSegment(value)}
              >
                {effectivenessSegments.map((key) => (
                  <ToggleButton key={key} value={key}>
                    {EFFECTIVENESS_SEGMENTS[key].label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            }
          />
          <GeoCardShowMap
            geoData={geofenceValue}
            data={activityMapData}
            onLoadData={onLoadActivityMapData}
            user_name={overview?.user_name}
            startDate={startDate}
            endDate={endDate}
            isMobileLayout={isMobileLayout}
            renderCard={({ onExpand }) => (
              <article className="rp-kpi-frame activity-geofence-card">
                <div className="rp-kpi-card">
                  <div className="rp-kpi-heading">
                    <span className="rp-section-icon">
                      <MapPin size={20} />
                    </span>
                    <h2>Actividades dentro de geocerca</h2>
                  </div>
                  <div className="rp-featured-metric rp-featured-metric--static">
                    <strong>{geofenceValue}</strong>
                  </div>
                  <p className="activity-geofence-copy">
                    <b>
                      Actividades realizadas dentro de geocerca{' '}
                      <Tooltip
                        title="Actividades realizadas dentro de la geocerca durante el período seleccionado. Este indicador no varía por tipo de actividad."
                        arrow
                        placement="top"
                      >
                        <span
                          className="rp-help"
                          tabIndex={0}
                          aria-label="Información sobre actividades realizadas dentro de geocerca"
                        >
                          <HelpOutlineIcon />
                        </span>
                      </Tooltip>
                    </b>
                    <small>Fijo, no varía por tipo de actividad</small>
                  </p>
                  <button type="button" className="activity-map-button" onClick={onExpand}>
                    Ver mapa
                    <ArrowUpRight size={20} aria-hidden="true" />
                  </button>
                </div>
              </article>
            )}
          />
        </div>
        {traceabilityCards.length > 0 && (
          <section
            className="rp-kpi-frame activity-totals-frame"
            aria-label="Totales de actividades"
          >
            <div className="activity-totals">
              {traceabilityCards.map((card) => {
                const activityCandidates = activityByCard[card.key] || []
                const activity =
                  activityCandidates.find((value) =>
                    (routeFilterData?.activities || []).includes(value)
                  ) || activityCandidates[0]
                const selected =
                  !selectedVisitKpi &&
                  !hasOtherFilters &&
                  activity &&
                  selectedActivityValues.length === 1 &&
                  selectedActivityValues.includes(activity)
                return (
                  <button
                    key={card.key}
                    type="button"
                    className="activity-total"
                    aria-pressed={activity ? Boolean(selected) : undefined}
                    disabled={!activity}
                    onClick={() => updateActivityFilter(selected ? null : activity)}
                  >
                    <span className="activity-total-heading">
                      <span className="rp-section-icon">{card.icon}</span>
                      {card.title}
                      <span className="activity-total-check">{selected ? '✓' : ''}</span>
                    </span>
                    <strong>{card.value}</strong>
                    <small>{card.helperText}</small>
                  </button>
                )
              })}
            </div>
          </section>
        )}
      </>
    )
  }

  const renderActivityDateRangeCard = () => (
    <RouteExecutionDateRangeCard
      title="Fecha de actividad"
      range={draftActivityDateRange}
      minDate={periodStart}
      maxDate={periodEnd}
      onChange={(nextRange) => setDraftActivityDateRange(constrainDateRange(nextRange))}
    />
  )

  return (
    <MapModalProvider>
      <div
        className={`rp-preview rp-connected-route activity-breakdown${isMobileLayout ? ' activity-breakdown-mobile' : ''}`}
      >
        {cardsSection(buildKpiData())}
        <div className="show-area-reports show-area-reports--tower-shell mt-30">
          <FilterUbiquaMui
            key={filterVersion}
            title="Filtrar actividades"
            searchPlaceholder="Buscar por código o nombre de cliente..."
            compactSearch
            filters={ubiquaFilters}
            appliedFilterSelections={appliedFilterSelections}
            isAnyFilterSelected={isAnyFilterSelected()}
            searchInput={query}
            defaultInlineFilterCount={3}
            drawerPreviewEmptyLabel="Todas las actividades"
            supplementaryAppliedGroups={supplementaryAppliedGroups}
            supplementaryFilterCount={1}
            supplementaryActiveCount={supplementaryActiveCount}
            supplementaryDraftCount={supplementaryDraftCount}
            onFilterChange={handleUbiquaFilterChange}
            onApplyFilters={applyDesktopFilters}
            onResetFilters={resetReactFilters}
            onDrawerOpen={() => {
              setDraftActivityDateRange(pickerValueToDateRange(selectedActivityDates))
            }}
            onDrawerReset={() => setDraftActivityDateRange(createEmptyDateRange())}
            onSearchChange={handleUbiquaSearchChange}
            onSearchEnter={handleUbiquaSearchEnter}
            onSearchClear={deleteSearch}
            onExport={
              isMobileLayout ? undefined : () => exportActivityBreakdownTableCsv(tableRows)
            }
            showExport={!isMobileLayout}
            exportDisabled={isActivityInfoFetching || tableRows.length === 0}
            exportTooltip="Descargar página actual con detalles en CSV"
            drawerExtraContent={
              <Stack spacing={1.5} sx={{ width: '100%', minWidth: 0, mt: 2.5 }}>
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid #dce6ef',
                    borderRadius: '18px',
                    bgcolor: '#f4f8fc'
                  }}
                >
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#597fa9', mb: 0.5 }}>
                    Período permitido
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#273e54' }}>
                    {periodStart
                      ? `${periodStart.format('DD/MM/YYYY')} – ${periodEnd.format('DD/MM/YYYY')}`
                      : `Hasta el ${periodEnd.format('DD/MM/YYYY')}`}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: '#7890a5', mt: 0.5 }}>
                    Selecciona tus fechas dentro de este rango.
                  </Typography>
                </Box>
                {renderActivityDateRangeCard()}
              </Stack>
            }
          />
        </div>
        <ActivityBreakdownTable
          rows={tableRows}
          isLoading={isActivityInfoFetching}
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          perPage={perPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={(_, nextPerPage) => onRowsPerPageChange?.(nextPerPage)}
          onPaginate={handlePaginationClick}
          onTransactionClick={handleTransactionClick}
          onOpenLocation={handleShow}
          formatActivityName={formatActivityName}
          isMobileLayout={isMobileLayout}
          hidePagination={isMobileLayout}
          expandedActivityKey={expandedActivityKey}
          onToggleExpand={toggleExpandedActivity}
        />
      </div>
      <ActivityReportMapModal
        show={show && Boolean(dataSelected)}
        onHide={handleClose}
        isMobileLayout={isMobileLayout}
        salesmanName={salesmanName}
        activity={dataSelected}
        formatActivityName={formatActivityName}
        shouldRenderMobileMap={shouldRenderMobileMap}
      />
    </MapModalProvider>
  )
}

export default ActivityBreakdownTab
